
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useTheme } from "@/components/ThemeProvider";
import { Logo } from "./Logo";
import { useUserRoles } from "@/hooks/useUserRoles";
import {
  LayoutDashboard,
  FileText, Layers, Tag, DollarSign, Users, Grid3X3, Shield, 
  Database, Settings, Package
} from "lucide-react";

interface MobileMenuProps {
  onOpenChange: (open: boolean) => void;
}

interface MobileNavLinkProps {
  to: string;
  title: string;
  active: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
}

function MobileNavLink({ to, title, active, onClick, icon }: MobileNavLinkProps) {
  const { theme } = useTheme();
  const is24Seven = theme === "24seven";

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center text-sm font-medium py-2 px-4 rounded-md transition-colors hover:bg-accent hover:text-accent-foreground ${
          isActive ? "bg-accent text-accent-foreground" : is24Seven ? "text-24seven-text" : "text-muted-foreground"
        }`
      }
    >
      {icon && <span className="mr-2">{icon}</span>}
      {title}
    </NavLink>
  );
}

export function MobileMenu({ onOpenChange }: MobileMenuProps) {
  const location = useLocation();
  const { theme } = useTheme();
  const is24Seven = theme === "24seven";
  const { useHasRoleQuery } = useUserRoles();
  const { data: isAdmin } = useHasRoleQuery("ADMIN");
  const { data: isDeveloper } = useHasRoleQuery("DEVELOPER");
  
  const hasAdminAccess = isAdmin || isDeveloper;
  
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-2">
        <Logo isAuthenticated={true} />
      </div>

      <ScrollArea className="flex-1 overflow-auto">
        <div className="space-y-2 py-4">
          <div className="px-3 py-2">
            <h2 className="mb-2 px-4 text-lg font-semibold tracking-tight">
              Main Navigation
            </h2>
            <div className="space-y-1">
              <MobileNavLink 
                to="/dashboard" 
                title="Dashboard" 
                active={location.pathname === "/dashboard"} 
                onClick={() => onOpenChange(false)}
                icon={<LayoutDashboard className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/contracts" 
                title="Contracts" 
                active={isActive("/contracts")} 
                onClick={() => onOpenChange(false)}
                icon={<FileText className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/services" 
                title="Services" 
                active={isActive("/services")} 
                onClick={() => onOpenChange(false)}
                icon={<Package className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/settings" 
                title="Settings" 
                active={isActive("/settings")} 
                onClick={() => onOpenChange(false)}
                icon={<Settings className="h-4 w-4" />}
              />
            </div>
          </div>
          <Separator />
          <div className="px-3 py-2">
            <h2 className="mb-2 px-4 text-lg font-semibold tracking-tight">
              Master Data
            </h2>
            <div className="space-y-1">
              <MobileNavLink 
                to="/categories" 
                title="Categories" 
                active={isActive("/categories")} 
                onClick={() => onOpenChange(false)}
                icon={<Tag className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/contract-types" 
                title="Contract Types" 
                active={isActive("/contract-types")} 
                onClick={() => onOpenChange(false)}
                icon={<Layers className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/currencies" 
                title="Currencies" 
                active={isActive("/currencies")} 
                onClick={() => onOpenChange(false)}
                icon={<DollarSign className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/contacts" 
                title="Contacts" 
                active={isActive("/contacts")} 
                onClick={() => onOpenChange(false)}
                icon={<Users className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/groups" 
                title="Groups" 
                active={isActive("/groups")} 
                onClick={() => onOpenChange(false)}
                icon={<Grid3X3 className="h-4 w-4" />}
              />
            </div>
          </div>
          
          {hasAdminAccess && (
            <>
              <Separator />
              <div className="px-3 py-2">
                <h2 className="mb-2 px-4 text-lg font-semibold tracking-tight">
                  Administration
                </h2>
                <div className="space-y-1">
                  <MobileNavLink 
                    to="/admin" 
                    title="Admin Panel" 
                    active={isActive("/admin")} 
                    onClick={() => onOpenChange(false)}
                    icon={<Shield className="h-4 w-4" />}
                  />

                  <MobileNavLink 
                    to="/admin/api-integration" 
                    title="API Integrations" 
                    active={isActive("/admin/api-integration")} 
                    onClick={() => onOpenChange(false)}
                    icon={<Database className="h-4 w-4" />}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
